import { FormFooter } from '@/components/basicComponents/formFooter';
import Input from '@/components/basicComponents/input';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { ApolloError, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FormFieldsWrapper } from '..';
import Dialog, { DialogStatus } from '../modals/dialog';
import { EDIT_STRATEGY_PAGE_INVESTMENT_QUERY, UPDATE_INVESTMENT_MANAGER_MUTATION } from '../queries';

interface IForm {
  managerId: string;
  newManagerId: string;
}

const defaultFormValues: IForm = {
  managerId: '',
  newManagerId: ''
};

interface ManagerModalPageProps {
  title: string;
  investmentId?: string;
  formData?: IForm | {};
  handleCloseEditModalPage: () => void;
}

export const ManagerModalPage = ({ title, investmentId, formData = {}, handleCloseEditModalPage }: ManagerModalPageProps) => {
  const { isMobile } = useResponsive();
  const { handleSubmit, getValues, setError, control, reset } = useForm<IForm>({ defaultValues: defaultFormValues });

  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogStatus, setDialogStatus] = useState<DialogStatus>();

  const [updateInvestmentManager] = useMutation(UPDATE_INVESTMENT_MANAGER_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [EDIT_STRATEGY_PAGE_INVESTMENT_QUERY]
  });

  useEffect(() => {
    if (!investmentId) return;

    reset((defaultFormData) => ({ ...defaultFormData, ...formData }));
  }, [formData]);

  const onCloseDialog = () => setDialogOpen(false);
  const onDialogSuccess = () => {
    reset();
    onCloseDialog();
    handleCloseEditModalPage();
  };

  const onUpdateInvestment = async (data: IForm) => {
    try {
      setDialogStatus(DialogStatus.LOADING);
      setDialogOpen(true);
      const { managerId, newManagerId } = data;

      await updateInvestmentManager({
        variables: {
          data: {
            managerId,
            newManagerId
          }
        }
      });

      setDialogStatus(DialogStatus.SUCCESS);
      setDialogOpen(true);
    } catch (error) {
      const err = error as ApolloError;
      if (err?.message === 'New manager already exists') {
        setError('newManagerId', { message: 'New manager already exists' });
        onCloseDialog();
        return;
      }
      setDialogStatus(DialogStatus.FAILED);
      setDialogOpen(true);
    }
  };

  return (
    <MainWrap>
      <Dialog
        isOpen={isDialogOpen}
        onClose={onCloseDialog}
        onSuccess={onDialogSuccess}
        reFetch={() => onUpdateInvestment(getValues())}
        status={dialogStatus}
      />
      <Header modalControl={<GoBackButton handleClose={handleCloseEditModalPage} backToTitle={''} />} />
      <PageTitle title={title ?? ''} />
      <PaddingWrap>
        <Form id="managerForm" onSubmit={handleSubmit(onUpdateInvestment, () => window.scrollTo(0, 0))}>
          <FormFieldsWrapper>
            <TwoColumnWrapper isMobile={isMobile}>
              <Controller
                name="newManagerId"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Manager is required'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input value={value || ''} onChange={onChange} label="Manager" errorText={error?.message} size="md" isRequiredField />
                )}
              />
            </TwoColumnWrapper>
          </FormFieldsWrapper>
          <FormFooter onCancel={handleCloseEditModalPage} disableSaveButton={false} />
        </Form>
      </PaddingWrap>
    </MainWrap>
  );
};

const Form = styled.form`
  padding: 50px 0;
  width: 100%;
  background: ${({ theme }) => theme.layer.base};
`;
